import { loggerInfo } from '@/modules/logger';
import { getLaunchAppUrl } from '@/modules/url';
import { getLangCode } from '@/modules/lang';
import { isInvalid, isObject } from '@/modules/validate';
import { inApp, isBrowser, getUAgent, getUaQuery, isTHS } from '@/modules/detector';

import { LogModule } from '@/data/config';
import { RedirectType } from '@/data/url';
import { getParseUrl } from '../url/common';

export * from './limit';
export * from './common';

/**
 * 获取地址的参数，指定的参数
 * 支持 浏览器，服务端
 * @param {string} name
 * @param {string} url (可选参数), 外部传入链接匹配
 */
export const getQuery = (name: string, url?: string): string | undefined => {
  const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)');
  const _url = url ?? (isBrowser() ? getBrowser().location.href : null);
  if (!_url) return;

  /** 解析地址 */
  const data = getParseUrl(_url);
  if (!data || !data?.search) return;

  const result = data.search.substr(1).match(reg);
  if (result === null) return;
  return unescape(result[2]);
};

/**
 * 深拷贝
 * @param origin
 * @param hasMap
 * @returns
 */
// eslint-disable-next-line
export function deepClone(origin: any, hasMap = new WeakMap()): any {
  if (!origin || typeof origin !== 'object' || origin === null) return origin; // 空或者非对象则返回本身

  if (origin instanceof Date) {
    return new Date(origin);
  }

  if (origin instanceof RegExp) {
    return new RegExp(origin);
  }

  // 如果这个对象已经被记录则直接返回
  if (hasMap.get(origin)) {
    return hasMap.get(origin);
  }

  // 这个对象还没有被记录，将其引用记录在 hasMap 中，进行拷贝
  const result = new origin.constructor();

  hasMap.set(origin, result); // 记录引用关系

  for (const key in origin) {
    // eslint-disable-next-line
    if (origin.hasOwnProperty(key)) {
      result[key] = deepClone(origin[key], hasMap);
    }
  }
  return result;
}

/**
 * @desc 函数防抖
 * @param func 函数
 * @param wait 延迟执行毫秒数
 * @param immediate true 表立即执行，false 表非立即执行
 */
export function debounce(func: () => void, wait: number, immediate?: boolean): unknown {
  let timeout: unknown;

  return function (this: unknown, ...args: any) {
    const context = this as unknown;

    if (timeout) clearTimeout(timeout as number);
    if (immediate) {
      const callNow = !timeout;
      timeout = setTimeout(() => {
        timeout = null;
      }, wait);
      if (callNow) func.apply(context, args);
    } else {
      timeout = setTimeout(function () {
        func.apply(context, args);
      }, wait);
    }
  };
}

/**
 * @desc 函数节流
 * @param func 函数
 * @param wait 延迟执行毫秒数 默认200
 * @param type 1 表时间戳版，2 表定时器版 默认1
 */

// eslint-disable-next-line @typescript-eslint/ban-types
export function throttle(func: Function, wait = 200, type = 1): Function {
  let previous: number, timeout: unknown;
  if (type === 1) {
    previous = 0;
  } else if (type === 2) {
    timeout;
  }
  return function (this: any, ...args: any[]) {
    if (type === 1) {
      const now = Date.now();

      if (now - previous > wait) {
        func.apply(this, args);
        previous = now;
      }
    } else if (type === 2) {
      if (!timeout) {
        timeout = setTimeout(() => {
          timeout = null;
          func.apply(this, args);
        }, wait);
      }
    }
  };
}

/**
 * 获取目录中的文件内容
 * @param context
 * @param extRegExp
 * @param keyRegExpCb
 * @returns
 */
export function importAll(
  context: any,
  extRegExp = /\.js$/,
  keyRegExpCb = (key: string) => key
): unknown {
  const modules: any = {};
  const replace = (str: string) => str.replace(/.\//g, '').replace(extRegExp, '');

  context.keys().forEach((key: string) => {
    const name = keyRegExpCb(replace(key));
    const module = context(key);
    if (name) modules[name] = module ? (module.default ? module.default : module) : {};
  });

  return modules;
}

/**
 * 等待
 * @param {string} timeSpan
 */
export const wait = (timeSpan = 1000): Promise<unknown> => {
  return new Promise(resolve => {
    setTimeout(resolve, timeSpan);
  });
};

/**
 * 获取浏览器
 */
export const getBrowser = () => {
  return isBrowser() ? window : global;
};

/**
 * 统一 H5 点击跳转（请用升级版本 commonRedirectV2）
 * cb 回调函数
 * params 请求参数
 * isNeedLaunch 开发环境调试，可以将 isNeedLaunch 设置为 true ，直接走 cb()
 * redirect 默认 href，可选 replace, 如果传'', 则不做处理
 */
export async function commonRedirect(
  cb: (...args: any) => void,
  data: Record<string | number, unknown>,
  isNeedLaunch = true,
  redirect = RedirectType.href
) {
  // 判断如果不在app内
  if (!inApp() && isNeedLaunch) {
    // 获取当前语言
    const locale = getLangCode();
    const launchUrl = await getLaunchAppUrl({
      locale,
      data,
      redirect,
    });
    return launchUrl;
  }
  return cb();
}

/**
 * 统一 H5 点击跳转
 * @params
 * cb 回调函数
 * params 请求参数
 * isNeedLaunch 开发环境调试，可以将 isNeedLaunch 设置为 true ，直接走 cb()
 * redirect 默认 href，可选 replace, 如果传'', 则不做处理
 */
export async function commonRedirectV2(
  cb: (...args: any) => void,
  data: Record<string | number, unknown>,
  options?: {
    isNeedLaunch?: boolean;
    redirect?: RedirectType;
    query?: Record<string | number, unknown>;
    url?: string;
  }
) {
  const _options = {
    isNeedLaunch: true,
    redirect: RedirectType.href,
    ...options,
  };

  // 判断如果不在app内
  if (!inApp() && _options.isNeedLaunch) {
    // 获取当前语言
    const locale = getLangCode();
    const launchUrl = await getLaunchAppUrl({
      locale,
      data,
      url: _options.url,
      redirect: _options.redirect,
      query: _options.query,
    });
    return launchUrl;
  }
  return cb();
}

/**
 * promise捕获方法
 * @param promise
 * @returns
 */
export function tryCatch<T>(promise: Promise<T>) {
  return promise
    .then(res => {
      return [null, res];
    })
    .catch(err => [err]);
}

// Mozilla/5.0 (iPhone; CPU iPhone OS 15_2 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 [HSTONG/2.4.700 AGENTID/200001 SELL_CLIENT/2 NetType/Wifi LANG/zh_CN THEME/light SCREEN_EDGE/{44,0,34,0}]
/**
 * 获取APP 高度位置
 * 相关链接
 * @param {string} options [customUA] Ua 浏览器
 * @returns
 */
export function getScreenEdge(options?: { customUA?: string }) {
  const { ua } = getUAgent(options?.customUA);

  const execResult = getUaQuery('SCREEN_EDGE', ua);

  if (execResult) {
    // SCREEN_EDGE/{44,0,34,0}
    const result = execResult.replace(/({|})/g, '').split(',');
    loggerInfo('输出的值', LogModule.Utils);

    return {
      top: !isInvalid(result[0]) ? Number(result[0]) : null,
      left: !isInvalid(result[1]) ? Number(result[1]) : null,
      bottom: !isInvalid(result[2]) ? Number(result[2]) : null,
      right: !isInvalid(result[3]) ? Number(result[3]) : null,
    };
  }
}

/**
 * 通过 Dpr 获取图片分辨率标识
 * @returns string '@2x'/'@3x'/''
 * @category Image
 */
export function getImageSignByDpr() {
  let sign = '@3x';
  // 非浏览器情况
  if (!isBrowser()) return sign;
  const devicePixelRatio = getBrowser().devicePixelRatio;
  if (devicePixelRatio <= 1) {
    sign = '';
  } else if (1 < devicePixelRatio && devicePixelRatio <= 2) {
    sign = '@2x';
  } else if (2 < devicePixelRatio && devicePixelRatio <= 3) {
    sign = '@3x';
  } else {
    sign = '@3x';
  }

  return sign;
}

/**
 * 根据语言 获取图片语言标识
 * @param options
 * @returns 例如：_zh-cn/_zh-hk/_en-us/_ar-sa
 * @category Image
 */
export function getImageSignByLang(options: {
  lang?: string;
  hook?: Function;
  supportLangs?: Array<string>;
  defaultLang?: string;
}): string {
  const lang = options?.lang;
  if (!lang) return '';
  const supportLangs = options?.supportLangs ?? [];
  const defaultLang = options?.defaultLang || '';
  let suffix = lang;
  // 不在支持的语言集合内不加标识 或 默认语言不加标识
  if (!supportLangs.includes(suffix) || lang === defaultLang) {
    suffix = '';
  }
  const hook = options?.hook;
  if (hook) {
    suffix = hook(suffix);
  }
  return suffix ? `_${suffix.toLowerCase()}` : '';
}

/**
 * 图片名称后添加标识(语言标识和dpr标识) 例如：logo.png ---> logo_ar-sa@2x.png
 * @param imgSrc
 * @param options
 * @returns
 * @category Image
 */
export function getImageNameBySign(
  imgSrc: string,
  options: {
    dpr?: boolean;
    lang?: string;
    hook?: Function;
    supportLangs?: Array<string>;
    defaultLang?: string;
  }
): string {
  const dpr = options.dpr ?? true;
  let src = imgSrc;
  // 适配语言和分辨率切换的图片
  src = imgSrc.replace(
    /(\..*)$/,
    `${getImageSignByLang(options)}${dpr ? getImageSignByDpr() : ''}$1`
  );
  return src;
}

/**
 * 深度合并对象
 */
export function deepObjectMerge(firstObj: Record<any, any>, secondObj: Record<any, any>) {
  for (const key in secondObj) {
    firstObj[key] =
      firstObj[key] && isObject(firstObj[key])
        ? deepObjectMerge(firstObj[key], secondObj[key])
        : secondObj[key];
  }
  return firstObj;
}

/**
 * 统一跳转处理
 */
export function commonJump(options: { url: string; redirect?: RedirectType }) {
  const redirect = options?.redirect ?? RedirectType.href;

  /** 同花顺需要做前缀处理 */
  if (isTHS()) {
    options.url = 'client://client.html?action=ymtz^mode=new^webid=2804^url=' + options.url;
  }

  if (redirect === RedirectType.href) {
    window.location.href = options.url;
  } else if (redirect === RedirectType.replace) {
    window.location.replace(options.url);
  }

  return options;
}
